const API_ORIGIN = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
const V1_BASE = `${API_ORIGIN}/v1`;

export const TOKEN_KEY = "tp_token";

export const getAccessToken = () => localStorage.getItem(TOKEN_KEY);
export const getApiRequestUrl = (path) => `${V1_BASE}${path}`;

const withQuery = (path, params = {}) => {
  const query = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") query.set(key, String(value));
  });
  const suffix = query.toString();
  return suffix ? `${path}?${suffix}` : path;
};

export class TreepletApiError extends Error {
  constructor(status, detail) {
    super(typeof detail === "string" ? detail : `Treeplet API request failed (${status})`);
    this.name = "TreepletApiError";
    this.status = status;
    this.detail = detail;
  }
}

export async function apiRequest(path, options = {}) {
  const token = getAccessToken();
  const headers = new Headers(options.headers || {});
  if (options.body && !headers.has("Content-Type")) headers.set("Content-Type", "application/json");
  if (token) headers.set("Authorization", `Bearer ${token}`);

  const response = await fetch(getApiRequestUrl(path), { ...options, headers });
  if (response.status === 204) return null;

  const body = await response.json().catch(() => null);
  if (!response.ok) {
    if (response.status === 401) window.dispatchEvent(new Event("treeplet:unauthorized"));
    throw new TreepletApiError(response.status, body?.detail);
  }
  return body;
}

export const login = (email, password) =>
  apiRequest("/auth/login", {
    method: "POST",
    body: JSON.stringify({ email, password }),
  });

export const getCurrentUser = () => apiRequest("/auth/me");
export const logout = () => apiRequest("/auth/logout", { method: "POST" });
export const getFields = () => apiRequest("/fields");
export const getFieldsHealth = () => apiRequest("/fields/health");
export const getFieldDevices = (fieldId) => apiRequest(`/fields/${encodeURIComponent(fieldId)}/devices`);
export const getFieldMildewRisk = (fieldId) => apiRequest(`/fields/${encodeURIComponent(fieldId)}/mildew-risk`);
export const getDevice = (deviceId) => apiRequest(`/devices/${encodeURIComponent(deviceId)}`);
export const getDeviceLatest = (deviceId) => apiRequest(`/devices/${encodeURIComponent(deviceId)}/latest`);
export const getDeviceTelemetry = (deviceId, from, to, interval = "15m") => {
  const query = new URLSearchParams({ from, to, interval });
  return apiRequest(`/devices/${encodeURIComponent(deviceId)}/telemetry?${query.toString()}`);
};

export const getAdminTenants = () => apiRequest("/admin/tenants");
export const getAdminTenantFields = (tenantId) =>
  apiRequest(`/admin/tenants/${encodeURIComponent(tenantId)}/fields`);
export const getAdminExplorerMetrics = () => apiRequest("/admin/explorer/metrics");
export const getAdminExplorerCustomers = (filters = {}) =>
  apiRequest(withQuery("/admin/explorer/customers", filters));
export const getAdminExplorerDevices = (filters = {}) =>
  apiRequest(withQuery("/admin/explorer/devices", filters));
export const getAdminExplorerMeasurements = (filters = {}) =>
  apiRequest(withQuery("/admin/explorer/measurements", filters));
export async function downloadAdminExplorerMeasurementsCsv(filters = {}) {
  const token = getAccessToken();
  const response = await fetch(getApiRequestUrl(withQuery("/admin/explorer/measurements.csv", filters)), {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!response.ok) {
    const body = await response.json().catch(() => null);
    if (response.status === 401) window.dispatchEvent(new Event("treeplet:unauthorized"));
    throw new TreepletApiError(response.status, body?.detail);
  }
  return response.blob();
}
export const createAdminCustomer = (payload) =>
  apiRequest("/admin/customers", { method: "POST", body: JSON.stringify(payload) });
export const onboardAdminDevice = (payload) =>
  apiRequest("/admin/device-onboardings", { method: "POST", body: JSON.stringify(payload) });
export const retryAdminDeviceCredential = (deviceId) =>
  apiRequest(`/admin/device-onboardings/${encodeURIComponent(deviceId)}/credential`, { method: "POST" });
export const getAdminMildewEvaluations = (filters = {}) =>
  apiRequest(withQuery("/admin/mildew/evaluations", filters));
export const getAdminMildewAlerts = (filters = {}) =>
  apiRequest(withQuery("/admin/mildew/alerts", filters));
export const getAdminMildewWeather = (filters = {}) =>
  apiRequest(withQuery("/admin/mildew/weather", filters));
export const getAdminMildewWorker = (filters = {}) =>
  apiRequest(withQuery("/admin/mildew/worker", filters));

export async function getFieldsWithDevices() {
  const fieldsEnvelope = await getFields();
  const fields = fieldsEnvelope.data || [];
  const deviceEnvelopes = await Promise.all(fields.map((field) => getFieldDevices(field.id)));
  return {
    fields,
    devices: deviceEnvelopes.flatMap((envelope) => envelope.data || []),
  };
}
